import React, { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { 
    FileSpreadsheet, 
    Upload, 
    ArrowRight, 
    Download, 
    RefreshCw, 
    CheckCircle2, 
    AlertCircle,
    Trash2,
    Info,
    ChevronRight,
    Search
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const FileUploadZone = ({ id, label, file, onUpload, onClear, accept = ".xlsx, .xls, .csv", description }) => (
    <div className="relative group">
        <div className={`p-8 border-2 border-dashed rounded-2xl transition-all duration-300 ${
            file 
                ? "bg-emerald-50 border-emerald-200" 
                : "bg-white border-slate-200 hover:border-navy-900/30 hover:bg-slate-50"
        }`}>
            <input 
                type="file" 
                id={id} 
                accept={accept} 
                className="hidden" 
                onChange={onUpload} 
            />
            
            <div className="flex flex-col items-center justify-center gap-4 text-center">
                <div className={`p-4 rounded-xl transition-transform group-hover:scale-110 duration-300 ${
                    file ? "bg-emerald-100 text-emerald-600" : "bg-navy-50 text-navy-900"
                }`}>
                    {file ? <CheckCircle2 size={32} /> : <Upload size={32} />}
                </div>
                
                <div>
                    <h3 className="font-bold text-navy-900">{file ? file.name : label}</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-[200px]">{description}</p>
                </div>

                {!file ? (
                    <label 
                        htmlFor={id} 
                        className="px-6 py-2 bg-navy-900 text-white text-xs font-bold rounded-lg cursor-pointer hover:bg-navy-800 transition-all shadow-md active:scale-95"
                    >
                        Browser Upload
                    </label>
                ) : (
                    <button 
                        onClick={onClear}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
            </div>
        </div>
        {file && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-lg border-2 border-white ring-4 ring-emerald-50/50">
                <CheckCircle2 size={12} />
            </div>
        )}
    </div>
);

export default function ExcelConverter() {
    // Files and Row Data
    const [sourceFile, setSourceFile] = useState(null);
    const [templateFile, setTemplateFile] = useState(null);
    const [sourceData, setSourceData] = useState([]);
    const [templateHeaders, setTemplateHeaders] = useState([]);
    const [sourceHeaders, setSourceHeaders] = useState([]);
    
    // Mapping state: { [templateHeader]: sourceHeader }
    const [mapping, setMapping] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState("");

    const handleSourceUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);
            
            if (jsonData.length > 0) {
                setSourceFile(file);
                setSourceData(jsonData);
                setSourceHeaders(Object.keys(jsonData[0]));
                setError("");
            } else {
                setError("Source file appears to be empty.");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleTemplateUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            
            // Get internal headers from template (row 1)
            const headers = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })[0];
            
            if (headers && headers.length > 0) {
                setTemplateFile(file);
                setTemplateHeaders(headers);
                
                // Initialize mapping if header names match exactly
                const initialMapping = {};
                headers.forEach(h => {
                    const match = sourceHeaders.find(sh => sh.toLowerCase() === h.toString().toLowerCase());
                    if (match) initialMapping[h] = match;
                });
                setMapping(initialMapping);
                setError("");
            } else {
                setError("Template file appears to have no headers.");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleMapChange = (tHeader, sHeader) => {
        setMapping(prev => ({
            ...prev,
            [tHeader]: sHeader
        }));
    };

    const generateFile = async () => {
        if (!sourceData.length || !templateHeaders.length) {
            setError("Please upload both source and template files.");
            return;
        }

        setIsGenerating(true);
        setError("");

        try {
            // Wait slightly for UI feel
            await new Promise(r => setTimeout(r, 1200));

            // Create new data based on mapping
            const convertedData = sourceData.map(row => {
                const newRow = {};
                templateHeaders.forEach(tHeader => {
                    const mappedSource = mapping[tHeader];
                    newRow[tHeader] = mappedSource ? row[mappedSource] : "";
                });
                return newRow;
            });

            // Create Workbook
            const worksheet = XLSX.utils.json_to_sheet(convertedData, { header: templateHeaders });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Converted Data");

            // Trigger Download
            const outputName = `Converted_${sourceFile.name.split(".")[0]}_Using_${templateFile.name.split(".")[0]}.xlsx`;
            XLSX.writeFile(workbook, outputName);

        } catch (err) {
            console.error(err);
            setError("Failed to generate file. Check console for details.");
        } finally {
            setIsGenerating(false);
        }
    };

    const reset = () => {
        setSourceFile(null);
        setTemplateFile(null);
        setSourceData([]);
        setSourceHeaders([]);
        setTemplateHeaders([]);
        setMapping({});
        setError("");
        setIsGenerating(false);
    };

    return (
        <div className="max-w-6xl mx-auto py-10 px-4">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-navy-900 text-white rounded-xl shadow-lg shadow-navy-900/20">
                            <RefreshCw size={24} className={isGenerating ? "animate-spin" : ""} />
                        </div>
                        <h1 className="text-3xl font-bold text-navy-900 font-serif">Excel Tool Converter</h1>
                    </div>
                    <p className="text-slate-500 max-w-xl">
                        A secure, in-browser utility to remap data from source spreadsheets into a specific template format. 
                        <span className="font-bold text-navy-900 mx-1">Privacy Guarantee:</span> No data is sent to or stored on any server.
                    </p>
                </div>
                <button 
                    onClick={reset}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                >
                    <RefreshCw size={16} /> Clear All
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Left: Input Areas */}
                <div className="lg:col-span-5 space-y-8">
                    <section>
                        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <ChevronRight size={14} /> 01. Input Files
                        </h2>
                        <div className="grid grid-cols-1 gap-4">
                            <FileUploadZone 
                                id="source-upload"
                                label="Upload Source (File X)"
                                file={sourceFile}
                                onUpload={handleSourceUpload}
                                onClear={() => { setSourceFile(null); setSourceData([]); setSourceHeaders([]); }}
                                description="CSV or XLSX containing raw data"
                            />
                            
                            <div className="flex justify-center -my-2 relative z-10">
                                <div className="bg-slate-100 p-2 rounded-full border-4 border-slate-50">
                                    <ArrowRight size={20} className="text-slate-400" />
                                </div>
                            </div>

                            <FileUploadZone 
                                id="template-upload"
                                label="Upload Template (SN)"
                                file={templateFile}
                                onUpload={handleTemplateUpload}
                                onClear={() => { setTemplateFile(null); setTemplateHeaders([]); setMapping({}); }}
                                description="XLSX defining the output headers"
                            />
                        </div>
                    </section>

                    {error && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-rose-50 border border-rose-200 p-4 rounded-xl flex gap-3 text-rose-800"
                        >
                            <AlertCircle className="shrink-0" />
                            <div>
                                <p className="text-sm font-bold">Heads up</p>
                                <p className="text-xs opacity-80">{error}</p>
                            </div>
                        </motion.div>
                    )}

                    <div className="bg-navy-900 rounded-2xl p-6 text-white shadow-xl shadow-navy-900/30">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/10 rounded-xl text-gold-400">
                                <Info size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-lg mb-1 italic">Security Protocol</h4>
                                <p className="text-sm text-navy-200 leading-relaxed">
                                    Files are processed locally in your browser's RAM. 
                                    Refreshing the page or closing the tab immediately wipes all session data from memory.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Mapping & Action Area */}
                <div className="lg:col-span-7">
                    <AnimatePresence mode="wait">
                        {!sourceFile || !templateFile ? (
                            <motion.div 
                                key="waiting"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="h-full min-h-[400px] border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center text-center p-10 bg-slate-50/50"
                            >
                                <div className="w-20 h-20 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 text-slate-300">
                                    <FileSpreadsheet size={40} />
                                </div>
                                <h3 className="text-xl font-bold text-navy-900 mb-2 font-serif">Awaiting Content</h3>
                                <p className="text-slate-400 max-w-sm text-sm">
                                    Please upload both the source data and template files to begin the mapping process.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="mapping"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col h-full"
                            >
                                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                    <div>
                                        <h2 className="font-bold text-navy-900">Define Column Mapping</h2>
                                        <p className="text-xs text-slate-500">Connect source fields to template columns</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center -space-x-2">
                                            <div className="w-8 h-8 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center text-emerald-600 font-bold text-[10px]">SRC</div>
                                            <div className="w-8 h-8 rounded-full bg-navy-100 border-2 border-white flex items-center justify-center text-navy-600 font-bold text-[10px]">TPL</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-6 max-h-[500px] custom-scrollbar space-y-3">
                                    {templateHeaders.map((tHeader, idx) => (
                                        <div 
                                            key={idx}
                                            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Target Template</p>
                                                <p className="font-bold text-navy-900 truncate">{tHeader}</p>
                                            </div>
                                            
                                            <div className="hidden sm:block text-slate-300 group-hover:text-navy-900 transition-colors">
                                                <ArrowRight size={18} />
                                            </div>

                                            <div className="flex-1">
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Source Column</p>
                                                <select 
                                                    className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 outline-none transition-all font-medium"
                                                    value={mapping[tHeader] || ""}
                                                    onChange={(e) => handleMapChange(tHeader, e.target.value)}
                                                >
                                                    <option value="">(Don't Fill)</option>
                                                    {sourceHeaders.map(sh => (
                                                        <option key={sh} value={sh}>{sh}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="p-6 bg-slate-50 border-t border-slate-100">
                                    <button 
                                        disabled={isGenerating}
                                        onClick={generateFile}
                                        className={`w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-bold transition-all shadow-xl active:scale-95 ${
                                            isGenerating 
                                                ? "bg-slate-200 text-slate-400 cursor-not-allowed" 
                                                : "bg-navy-900 text-white hover:bg-navy-800 shadow-navy-900/20"
                                        }`}
                                    >
                                        {isGenerating ? (
                                            <>
                                                <RefreshCw size={22} className="animate-spin text-navy-900" />
                                                Processing Data...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={22} />
                                                Generate & Download Converted File
                                            </>
                                        )}
                                    </button>
                                    <p className="text-[10px] text-center text-slate-400 mt-4 uppercase tracking-[0.2em] font-medium">
                                        System Integrity Verified • No Data Persistence
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
